import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Language as DomainIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Dns as DnsIcon
} from '@mui/icons-material';

const SubdomainScanner = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [maxResults, setMaxResults] = useState(10);
  const [expandedRows, setExpandedRows] = useState({});

  const handleScan = async () => {
    if (!domain.trim()) {
      setError('Please enter a domain name');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}subdomains/discover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domain: domain.trim(),
          max_results: maxResults,
          use_external_tools: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(`Scan failed: ${err.message}`);
      console.error('Subdomain scan error:', err);
    } finally {
      setLoading(false);
    }
  };


  const formatDuration = (seconds) => {
    return seconds < 60 ? `${seconds.toFixed(1)}s` : `${Math.floor(seconds / 60)}m ${(seconds % 60).toFixed(1)}s`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DomainIcon />
          Subdomain Scanner
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Discover subdomains for any domain using multiple discovery methods
        </Typography>
      </Box>

      {/* Scan Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Domain Name"
                placeholder="example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                disabled={loading}
                onKeyPress={(e) => e.key === 'Enter' && handleScan()}
                sx={{ height: 56 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max Results"
                value={maxResults}
                onChange={(e) => setMaxResults(parseInt(e.target.value) || 10)}
                disabled={loading}
                inputProps={{ min: 5, max: 100, step: 5 }}
                helperText={`Limit results to ${maxResults} subdomains`}
                sx={{ height: 56 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleScan}
                disabled={loading}
                sx={{ height: 56 }}
              >
                {loading ? 'Scanning...' : 'Scan Domain'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading Progress */}
      {loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">Scanning {domain}...</Typography>
            </Box>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Checking subdomains using built-in wordlist, external tools, and Certificate Transparency logs
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results Summary */}
      {results && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {Math.min(results.subdomains.length, maxResults)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Scanned (Max: {maxResults})
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {results.subdomains.slice(0, maxResults).filter(sub => sub.is_active).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Subdomains
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">
                  {results.subdomains.slice(0, maxResults).filter(sub => !sub.is_active).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inactive
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main">
                  {formatDuration(results.scan_duration)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Scan Duration
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Results Table */}
      {results && results.subdomains && results.subdomains.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Discovered Subdomains ({results.subdomains.slice(0, maxResults).length})
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                onClick={handleScan}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell><strong>Subdomain</strong></TableCell>
                    <TableCell><strong>IP Address (A Record)</strong></TableCell>
                    <TableCell><strong>DNS Records</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.subdomains.slice(0, maxResults).map((subdomain, index) => (
                    <React.Fragment key={index}>
                      <TableRow 
                        sx={{ 
                          '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }))}
                            disabled={!subdomain.dns_records}
                          >
                            {expandedRows[index] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {subdomain.subdomain}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {subdomain.ip_address || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {subdomain.dns_records ? (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {Object.keys(subdomain.dns_records).map((recordType) => (
                                <Chip
                                  key={recordType}
                                  label={recordType}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '10px', height: '20px' }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={subdomain.is_active ? <ActiveIcon /> : <InactiveIcon />}
                            label={subdomain.is_active ? 'Active' : 'Inactive'}
                            color={subdomain.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Visit Subdomain">
                              <IconButton
                                size="small"
                                onClick={() => window.open(`https://${subdomain.subdomain}`, '_blank')}
                                disabled={!subdomain.is_active}
                              >
                                <DomainIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse in={expandedRows[index]} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DnsIcon /> DNS Records for {subdomain.subdomain}
                              </Typography>
                              {subdomain.dns_records ? (
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                  {Object.entries(subdomain.dns_records).map(([recordType, records]) => (
                                    <Grid item xs={12} md={6} key={recordType}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                                            {recordType} Records
                                          </Typography>
                                          {Array.isArray(records) && records.length > 0 ? (
                                            <Box sx={{ pl: 2 }}>
                                              {records.map((record, idx) => (
                                                <Typography
                                                  key={idx}
                                                  variant="body2"
                                                  sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '12px',
                                                    mb: 0.5,
                                                    wordBreak: 'break-all'
                                                  }}
                                                >
                                                  {typeof record === 'object' ? (
                                                    <Box component="span">
                                                      {recordType === 'MX' && (
                                                        `Priority: ${record.priority}, Server: ${record.server}`
                                                      )}
                                                      {recordType === 'SOA' && (
                                                        `Primary NS: ${record.mname}, Admin: ${record.rname}, Serial: ${record.serial}`
                                                      )}
                                                    </Box>
                                                  ) : (
                                                    record
                                                  )}
                                                </Typography>
                                              ))}
                                            </Box>
                                          ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                                              No records
                                            </Typography>
                                          )}
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No DNS records available
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* No Results */}
      {results && results.subdomains && results.subdomains.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No subdomains found for {results.domain}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try scanning a different domain, increase max results limit, or check if the domain is valid
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SubdomainScanner;
